// Frontend mirror of the server call-out charge calculator
// (server/src/modules/pricing/fees.ts -> calculateCallOutCharge). Used for the live
// charge PREVIEW on the office charge panel and the engineer PWA call-out sheet (the
// PWA must work offline, so the preview is computed client-side rather than fetched).
// The SERVER remains authoritative: the office's PUT /api/callouts/:id/charge recomputes
// and stores the charge. This file deliberately duplicates the locked rules so the two
// stay in lock-step; both carry the same test cases. All figures are in pence.

export const FIRST_HOUR = 163; // GBP, ex VAT
export const ADDITIONAL_HOUR = 125;
export const TRAVEL_RATE = 60; // per hour, flat (never doubles out of hours)
export const NON_CONTRACT_MULT = 2;
export const OOH_MULT = 2;

export type CalloutChargeTier = "CORE" | "CARE" | "COMPLETE";

// The tier discount is a CALL-OUT benefit and does not reach REPAIR labour (Session AMH, Director
// decision 25 August 2026). This mirror needs no work-type parameter of its own, and deliberately
// does not have one: it previews a CallOut record's charge panel and the engineer PWA's call-out
// sheet, both of which price a reactive call-out. Repair labour is priced server-side only, by
// computeCalloutReportCharge (the attendance report) and billing.ts billableAmount (the completed
// repair job's draft), each ring-fenced through tierDiscountFor in server/src/modules/pricing/
// fees.ts. Adding an unused parameter here would be dead code on an offline-capable surface; if a
// repair basis ever reaches the charge panel, mirror tierDiscountFor rather than inlining a second
// rule. Everything below is byte-identical to before that session.
export const CALLOUT_TIER_DISCOUNT: Record<CalloutChargeTier, number> = {
  CORE: 0,
  CARE: 0.15,
  COMPLETE: 0.25,
};

export interface CallOutChargeInput {
  arrivalTime: Date;
  departureTime: Date;
  travelHours: number;
  tier: CalloutChargeTier;
  isNonContract: boolean;
  isOutOfHours?: boolean;
}

export interface CallOutChargeResult {
  durationHours: number;
  labourHours: number;
  isOutOfHours: boolean;
  firstHourRate: number; // pence
  additionalHourRate: number; // pence
  travelCharge: number; // pence
  labourCharge: number; // pence
  totalCharge: number; // pence
  breakdown: string;
}

// Out of hours = weekend OR before 09:00 OR at/after 17:00 on a weekday. Bank holidays
// are confirmed by the office per job (the isOutOfHours flag), never hardcoded.
export function deriveOutOfHours(at: Date): boolean {
  const day = at.getDay();
  if (day === 0 || day === 6) return true;
  const hour = at.getHours();
  return hour < 9 || hour >= 17;
}

// ---- Repair labour (Session RC1, Director's rules R1/R2, 23 October 2026) ----------------------------------------
// Mirrors server/src/modules/pricing/fees.ts REPAIR_EVENING_START_HOUR / isRepairOutOfHoursClock / repairHourRate /
// repairsPublishedText exactly; repairPricing.test.ts binds the two. A repair (the return visit) is GBP 125.00 an hour
// on weekdays before 18:00 and GBP 250.00 an hour on weekdays from 18:00, at weekends and on bank holidays, in contract
// or not: no non-contract multiplier and no tier discount. The figures are read from ADDITIONAL_HOUR and OOH_MULT
// above, never typed. A SEPARATE rule from deriveOutOfHours above, which is the call-out card's 09:00 to 17:00.
export const REPAIR_EVENING_START_HOUR = 18;

export function isRepairOutOfHoursClock(dayOfWeek: number, hour: number): boolean {
  if (dayOfWeek === 0 || dayOfWeek === 6) return true;
  return hour >= REPAIR_EVENING_START_HOUR;
}

export function repairHourRate(isOutOfHours: boolean): number {
  return ADDITIONAL_HOUR * (isOutOfHours ? OOH_MULT : 1);
}

export function repairsPublishedText(money: (gbp: number) => string): string {
  return (
    "a call-out lasts while our engineer stays on site, and everything done on that attendance, including any repair, " +
    "is charged at the call-out rates above. If the engineer has to leave and come back (for parts, a second engineer, " +
    "or because the work is paused), the return visit is a repair. Repair labour is " +
    `${money(repairHourRate(false))} per hour on weekdays before ${REPAIR_EVENING_START_HOUR}:00 and ${money(repairHourRate(true))} per hour on weekdays from ` +
    `${REPAIR_EVENING_START_HOUR}:00, at weekends and on bank holidays, whether or not you hold a contract. There is no first hour premium on ` +
    "repair labour and maintenance tier discounts do not apply to it."
  );
}

// LABOUR IS BILLED IN 15-MINUTE STEPS, ROUNDED UP, WITH A ONE-HOUR MINIMUM (Director decision,
// 5 October 2026; the half hour until then). Mirrors fees.ts roundUpToLabourIncrement /
// billableLabourHours exactly, epsilon included, so the panel's live preview and the stored charge
// agree to the penny.
export const LABOUR_INCREMENT_MINUTES = 15;
const LABOUR_INCREMENTS_PER_HOUR = 60 / LABOUR_INCREMENT_MINUTES;
const LABOUR_ROUNDING_EPSILON = 1e-9;

// Mirrors fees.ts LABOUR_INCREMENT_SENTENCE word for word (labourRounding.test.ts).
export const LABOUR_INCREMENT_SENTENCE = `Time on site is charged in ${LABOUR_INCREMENT_MINUTES}-minute steps, rounded up, with a minimum of one hour.`;

export function roundUpToLabourIncrement(hours: number): number {
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  return Math.ceil(hours * LABOUR_INCREMENTS_PER_HOUR - LABOUR_ROUNDING_EPSILON) / LABOUR_INCREMENTS_PER_HOUR;
}

export function billableLabourHours(hoursOnSite: number): number {
  return Math.max(1, roundUpToLabourIncrement(hoursOnSite));
}

// "15 min", "1 hr", "1 hr 15 min". Mirrors fees.ts labourMinutesLabel.
export function labourMinutesLabel(hours: number): string {
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function poundsToPence(pounds: number): number {
  return Math.round(pounds * 100);
}

// The in-hours first-hour rate a tier pays, read from the two constants above (the same arithmetic as
// calculateCallOutCharge), and written for a customer: a whole figure with no decimals (£163), a fractional one
// to 2dp (£138.55, £122.25). Bound to CALLOUT_FIRST_HOUR in server billing.ts by AcceptQuote.test.tsx.
export function firstHourFigure(tier: CalloutChargeTier): string {
  const pence = Math.round(poundsToPence(FIRST_HOUR) * (1 - CALLOUT_TIER_DISCOUNT[tier]));
  const pounds = pence / 100;
  return `£${pounds.toLocaleString("en-GB", { minimumFractionDigits: Number.isInteger(pounds) ? 0 : 2, maximumFractionDigits: 2 })}`;
}

function penceToGbp(pence: number): string {
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function calculateCallOutCharge(input: CallOutChargeInput): CallOutChargeResult {
  const ms = input.departureTime.getTime() - input.arrivalTime.getTime();
  const durationHours = Number.isFinite(ms) ? Math.max(0, ms / 3_600_000) : 0;
  const labourHours = billableLabourHours(durationHours);
  const additionalHours = Math.max(0, labourHours - 1);

  const isOutOfHours = input.isOutOfHours ?? deriveOutOfHours(input.arrivalTime);
  const labourMult = (isOutOfHours ? OOH_MULT : 1) * (input.isNonContract ? NON_CONTRACT_MULT : 1);
  // Session AKN (Director decision): travel now compounds for non-contract out of hours. The OOH
  // factor is gated behind isNonContract (unlike labourMult): in-contract OOH never doubles
  // travel; non-contract OOH doubles it twice over (GBP 120/hr -> GBP 240/hr).
  const travelMult = input.isNonContract ? NON_CONTRACT_MULT * (isOutOfHours ? OOH_MULT : 1) : 1;
  const discountFactor = 1 - CALLOUT_TIER_DISCOUNT[input.tier];

  const firstHourRate = Math.round(poundsToPence(FIRST_HOUR) * labourMult * discountFactor);
  const additionalHourRate = Math.round(poundsToPence(ADDITIONAL_HOUR) * labourMult * discountFactor);
  const additionalCharge = Math.round(additionalHourRate * additionalHours);
  const labourCharge = firstHourRate + additionalCharge;

  const travelHours = roundUpToLabourIncrement(Number.isFinite(input.travelHours) ? Math.max(0, input.travelHours) : 0);
  const travelCharge = Math.round(poundsToPence(TRAVEL_RATE) * travelHours * travelMult);

  const totalCharge = labourCharge + travelCharge;

  const parts: string[] = [`First hour${isOutOfHours ? " (out of hours)" : ""}: ${penceToGbp(firstHourRate)}`];
  if (additionalHours > 0) {
    parts.push(`Additional ${labourMinutesLabel(additionalHours)}: ${penceToGbp(additionalCharge)}`);
  }
  if (travelCharge > 0) parts.push(`Travel ${travelHours} hr: ${penceToGbp(travelCharge)}`);
  if (input.isNonContract) parts.push("Non-contract rate applied");
  if (input.tier !== "CORE" && !input.isNonContract) {
    parts.push(`${input.tier === "CARE" ? "Care" : "Complete"} tier discount applied`);
  }
  parts.push(`Total: ${penceToGbp(totalCharge)} ex VAT`);

  return {
    durationHours,
    labourHours,
    isOutOfHours,
    firstHourRate,
    additionalHourRate,
    travelCharge,
    labourCharge,
    totalCharge,
    breakdown: parts.join(". ") + ".",
  };
}

// Convenience for the UI: format pence as GBP.
export function pence(p: number | null | undefined): string {
  if (p == null) return "-";
  return penceToGbp(p);
}

// ---- Manual invoice reference (Session AHJ) ---------------------------------

// Mirror of server/src/modules/callouts/calloutReport.ts. The office types its own Xero invoice
// number on a report whose charges are hidden (the invoice was raised in Xero by hand), and it
// becomes the report reference in place of the internal "CO-<cuid>".
//
// Mirrored so the form refuses a bad shape while it is being typed rather than after it is
// submitted. server/src/modules/callouts/manualInvoiceRefParity.test.ts fails if the two drift,
// because a client that accepts what the server refuses sends the office back to a filled-in form.
export const MANUAL_INVOICE_REF_PATTERN = /^INV-\d+$/;
// The server's zod .max(40), which runs on the RAW string before the trim/uppercase transform.
export const MANUAL_INVOICE_REF_MAX_RAW_LENGTH = 40;
export const MANUAL_INVOICE_REF_MESSAGE = 'Enter the Xero invoice number in the form INV-2431, or leave it blank to use the platform reference.';

// The error to show for what is currently typed, or null when it is acceptable (blank included:
// the field is optional, and blank simply keeps the platform's own reference). Pure.
export function manualInvoiceRefError(raw: string | null | undefined): string | null {
  // The server applies its 40-character ceiling to the RAW string, BEFORE trimming, so a value
  // padded past 40 by a paste is refused there. Mirrored here (also raw) rather than left to the
  // server: the adversarial review found this divergence, and it is exactly the shape this pair
  // exists to prevent, a client that accepts what the server refuses.
  if ((raw?.length ?? 0) > MANUAL_INVOICE_REF_MAX_RAW_LENGTH) return MANUAL_INVOICE_REF_MESSAGE;
  const value = raw?.trim().toUpperCase() ?? "";
  if (!value) return null;
  return MANUAL_INVOICE_REF_PATTERN.test(value) ? null : MANUAL_INVOICE_REF_MESSAGE;
}
